/* eslint-disable @typescript-eslint/no-magic-numbers */
import { computed, inject, Injectable, NgZone, signal } from '@angular/core';
import { BehaviorSubject, catchError, debounceTime, EMPTY, Observable, of, Subject, switchMap, tap, withLatestFrom } from 'rxjs';
import { EnhancePatientNotePromptType } from '@ffd/api-client';
import { ConfigService, HistoryStackService, ToastService, TokenService } from '@libs/shared-services';
import { TranslateService } from '@ngx-translate/core';
import { DeepGramSpeechRecognitionService } from '@libs/speech-recognition';
import { Dialog } from '@angular/cdk/dialog';
import { fromReadableStream } from 'libs/notes/src/lib/components/notes-screen/notes-utils';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';

const MODIFY_NOTE_DEBOUNCE_TIME_MS = 1000;

export interface TextHistoryElementWithCursor {
  text: string;
  cursorOffset: number;
}

interface TextareaWithAIState {
  text: string;
  isGenerating: boolean;
  lastFocusedCursorOffset: number;
  allowErrorModal: boolean;
}

@Injectable()
export class TextareaWithAIService extends ComponentStore<TextareaWithAIState> {
  public configService = inject(ConfigService);
  public tokenService = inject(TokenService);
  public dialog = inject(Dialog);
  public ngZone = inject(NgZone);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly speechRecognitionService = inject(DeepGramSpeechRecognitionService);
  private readonly historyStackService = inject<HistoryStackService<TextHistoryElementWithCursor>>(HistoryStackService);

  private readonly enhanceRange = signal<{ textStart?: number; textEnd?: number; originalNote?: string }>({
    textStart: undefined,
    textEnd: undefined,
    originalNote: undefined,
  });

  // Accumulator for enhanced text chunks during streaming
  private enhancedTextAccumulator: string = '';

  constructor() {
    super({
      text: '',
      isGenerating: false,
      lastFocusedCursorOffset: 0,
      allowErrorModal: true,
    });
  }

  private readonly transcription = this.speechRecognitionService.transcription;
  private abortController = new AbortController();
  private abortSignal = this.abortController.signal;
  private readonly noteTypingSubject$ = new Subject<{ notes: string; autoSave?: boolean }>();

  // controlledNote$ is the note that is currently being edited, but it may not contain the changes which has been typed in manually in the text field.
  // because we want to keep the cursor position, we do not re-render the UI when the note is changed manually
  public controlledNote$ = new BehaviorSubject<string>('');
  public redoDisabledSignal = computed(() => this.historyStackService.isFirstPosition() || this.isGeneratingSignal());
  public undoDisabledSignal = computed(() => this.historyStackService.isLastPosition() || this.isGeneratingSignal());
  public isGeneratingSignal = computed(() => this.state().isGenerating);
  public currentHistoryElementSignal = computed(() => this.historyStackService.getCurrentItem());
  public lastFocusedCursorOffsetSignal = this.selectSignal((state) => state.lastFocusedCursorOffset);

  public savedNoteSignal = signal<string | null>(null);

  public init = this.updater((_): TextareaWithAIState => {
    const initialText = this.state().text.length > 0 ? this.state().text : '';

    this.setControlledNote(initialText);
    this.updateLastFocusedCursorOffset(initialText.length);

    this.historyStackService.init({
      text: initialText,
      cursorOffset: initialText.length,
    });

    return {
      text: initialText,
      isGenerating: false,
      lastFocusedCursorOffset: initialText.length,
      allowErrorModal: true,
    };
  });

  // Voice typing related functions:

  public readonly toggleSpeechRecognitionListening = this.effect(($) =>
    $.pipe(
      tap(() => {
        this.speechRecognitionService.toggleSpeechRecognitionListening({
          onStop: (transcription) => {
            const selectionStartOffset = this.state().lastFocusedCursorOffset;
            const editModeText = this.controlledNote$.value;
            const newNote =
              selectionStartOffset === 0
                ? `${transcription}${editModeText}`
                : `${editModeText.slice(0, selectionStartOffset)}${transcription}${editModeText.slice(selectionStartOffset)}`;

            this.addToHistoryStack({ text: newNote, cursorOffset: this.state().lastFocusedCursorOffset });
            this.setControlledNote(newNote);
            this.saveNotes();
            this.updateLastFocusedCursorOffset(selectionStartOffset + transcription.length);
          },
        });
      }),
    ),
  );

  public selectMicrophone(deviceId: string): void {
    this.speechRecognitionService.selectMicrophone(deviceId);
  }

  public openAudioInputErrorModal(): void {
    this.speechRecognitionService.openAudioInputErrorModal();
  }

  public updateLastFocusedCursorOffset = this.updater((state, lastFocusedCursorOffset: number): TextareaWithAIState => {
    return { ...state, lastFocusedCursorOffset };
  });

  // AI related functions:

  private readonly appendEnhancedNote = (noteChunk: string): void => {
    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Update the display but don't update the history stack yet - that happens on completion
    this.controlledNote$.next(this.enhancedTextAccumulator);
  };

  private readonly appendEnhancedNoteWithTextRange = (noteChunk: string, textStart: number, textEnd: number): void => {
    const enhanceRangeValue = this.enhanceRange();
    const originalNote = enhanceRangeValue.originalNote || '';

    // Accumulate the enhanced text chunks
    this.enhancedTextAccumulator += noteChunk;

    // Construct and display the note with the accumulated enhanced text
    // but don't update the history stack yet - that happens on completion
    const newNoteText = originalNote.slice(0, textStart) + this.enhancedTextAccumulator + originalNote.slice(textEnd);
    this.controlledNote$.next(newNoteText);
  };

  public abortEnhance = this.effect(($) => {
    return $.pipe(
      withLatestFrom(this.select((state) => state.isGenerating)),
      tap(([_, isGenerating]) => {
        if (isGenerating) {
          this.abortController.abort();
        }
      }),
    );
  });

  // TODO: fix error handling, should be able to do another enhance after error
  public enhanceNote = this.effect<{
    noteInput: string;
    enhanceType: EnhancePatientNotePromptType;
    textStart?: number;
    textEnd?: number;
    fullNote?: string;
  }>(
    (
      $: Observable<{
        noteInput: string;
        enhanceType: EnhancePatientNotePromptType;
        textStart?: number;
        textEnd?: number;
        fullNote?: string;
      }>,
    ) => {
      const encoder = new TextDecoder();
      return $.pipe(
        tap(({ fullNote, textStart, textEnd }) => {
          // Store the current note before enhancement starts
          const currentNote = fullNote || this.controlledNote$.value;
          this.addToHistoryStack({
            text: currentNote,
            cursorOffset: this.state().lastFocusedCursorOffset,
          });

          // Store the original note and range info
          this.enhanceRange.set({
            textStart,
            textEnd,
            originalNote: textStart !== undefined && textEnd !== undefined ? currentNote : undefined,
          });

          // Reset the accumulator for new enhancement
          this.enhancedTextAccumulator = '';

          this.patchState({ isGenerating: true });
        }),
        concatLatestFrom(() => this.tokenService.getAccessToken$({ isUserActivity: true })),
        switchMap(([{ noteInput, enhanceType }, token]) => {
          // TODO: replace with path for new patient notes stream when it is available
          return fetch(`${this.configService.config()?.apiBasePath}/v1/patient-notes/enhance-stream`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              note: noteInput,
              enhanceType: enhanceType,
            }),
          });
        }),
        switchMap((response) => {
          if (!response.ok) {
            if (response.status === 400) {
              this.toastService.warning(this.translateService.instant('notes_empty_note_error_message'));
              this.addToHistoryStack({
                text: '',
                cursorOffset: this.state().lastFocusedCursorOffset,
              });
            }
            if (response.status === 403) {
              this.toastService.error(this.translateService.instant('notes_ai_token_exceeded'));
            }
            this.patchState({ isGenerating: false });
            return EMPTY;
          }

          return response.body
            ? fromReadableStream(response.body, { ngZone: this.ngZone, signal: this.abortSignal }).pipe(
                tap({
                  next: (chunk) => {
                    const text = encoder.decode(chunk);
                    if (this.enhanceRange().textStart !== undefined && this.enhanceRange().textEnd !== undefined) {
                      this.appendEnhancedNoteWithTextRange(text, this.enhanceRange().textStart!, this.enhanceRange().textEnd!);
                    } else {
                      this.appendEnhancedNote(text);
                    }
                  },
                  error: () => {
                    console.error('Error while enhancing note');
                    this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });
                  },
                  complete: () => {
                    this.abortController = new AbortController();
                    this.abortSignal = this.abortController.signal;
                    this.patchState({ isGenerating: false });

                    // Now that enhancement is complete, add the final result to history stack
                    const finalNote = this.controlledNote$.value;
                    this.addToHistoryStack({
                      text: finalNote,
                      cursorOffset: this.state().lastFocusedCursorOffset,
                    });

                    this.saveNotes();
                  },
                }),
              )
            : EMPTY;
        }),
        catchError((error) => {
          console.error('Error while enhancing note', error);
          this.toastService.error(this.translateService.instant('health_history_notes_section_enhance_error'));
          this.abortController = new AbortController();
          this.abortSignal = this.abortController.signal;
          this.patchState({ isGenerating: false });
          return EMPTY;
        }),
      );
    },
  );

  // History related functions:

  private readonly addToHistoryStack = (note: TextHistoryElementWithCursor): void => {
    this.historyStackService.push(note);
  };

  public setControlledNote(note: string): void {
    this.controlledNote$.next(note);
  }

  public handleNoteTyping = this.effect((_$) => {
    return this.noteTypingSubject$.pipe(
      switchMap(({ notes, autoSave }) => {
        const source$ = of({ notes });

        return autoSave ? source$.pipe(debounceTime(MODIFY_NOTE_DEBOUNCE_TIME_MS)) : source$;
      }),
      tap(({ notes }) => {
        this.addToHistoryStack({
          text: notes,
          cursorOffset: this.state().lastFocusedCursorOffset,
        });
      }),
      tap(({ notes }) => {
        this.patchState({ text: notes });
        this.saveNotes();
      }),
    );
  });

  public noteTyping(data: { notes: string; autoSave?: boolean }): void {
    this.noteTypingSubject$.next(data);
  }

  public undo = (): void => {
    const currentItem = this.historyStackService.undo();
    if (currentItem) {
      const lastNote = currentItem.text;
      this.setControlledNote(lastNote);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public redo = (): void => {
    const currentItem = this.historyStackService.redo();
    if (currentItem) {
      const nextNote = currentItem.text;
      this.setControlledNote(nextNote);
      this.updateLastFocusedCursorOffset(currentItem.cursorOffset);
      this.saveNotes();
    }
  };

  public resetHistory = (): void => {
    this.historyStackService.reset();
  };

  public saveNotes(): void {
    this.savedNoteSignal.set(this.historyStackService.getCurrentItem()?.text ?? null);
  }
}
